import { type AnalyticsAppFilter } from '@carsfinders/contracts';
import { useMemo, useState, type ReactNode } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { cn } from '../clients/utils';
import { PanelEmpty, PanelError } from '../finance/components/panel-state';
import { useAnalyticsSummary } from './api';
import { DailyChart } from './components/daily-chart';
import { KpiTiles, KpiTilesSkeleton, buildKpiColumns } from './components/kpi-tiles';
import { SessionsTable } from './components/sessions-table';
import { UsersLastSeen } from './components/users-last-seen';
import { denseDays, kyivToday, periodRange } from './format';

export const ANALYTICS_PERIODS = [7, 30, 90] as const;
export type AnalyticsPeriod = (typeof ANALYTICS_PERIODS)[number];
const DEFAULT_PERIOD: AnalyticsPeriod = 30;
/** The KPI tiles always read the last 30 days, whatever the chart shows. */
const KPI_DAYS = 30;

const APPS: readonly AnalyticsAppFilter[] = ['all', 'auction', 'marketplace'];

function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/**
 * «Аналітика» (`/analytics`, spec §8) — who visits the auction and the
 * marketplace: KPI for today / 7 / 30 days, the daily chart for the chosen
 * period, the session feed and the registered users by last visit.
 *
 * Days are Kyiv days (the rollup's own boundary). Period uniques are sums of
 * daily uniques — the tiles say so rather than imply a distinct count.
 */
export function AnalyticsPage() {
  const [period, setPeriod] = useState<AnalyticsPeriod>(DEFAULT_PERIOD);
  const [app, setApp] = useState<AnalyticsAppFilter>('all');
  const today = kyivToday();

  const range = periodRange(period, today);
  const kpiRange = periodRange(KPI_DAYS, today);
  const summary = useAnalyticsSummary({ ...range, app });
  // Same key as `summary` when the period is 30 days — react-query dedups it.
  const kpiSummary = useAnalyticsSummary({ ...kpiRange, app });

  const kpiColumns = useMemo(
    () => (kpiSummary.data ? buildKpiColumns(kpiSummary.data.days, today) : null),
    [kpiSummary.data, today],
  );
  const chartDays = useMemo(
    () => (summary.data ? denseDays(summary.data.days, range.from, range.to) : []),
    [summary.data, range.from, range.to],
  );

  if (isForbidden(summary.error) || isForbidden(kpiSummary.error)) return <AccessDenied />;

  const chartEmpty = summary.data !== undefined && summary.data.totals.sessions === 0;

  return (
    <>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
            {t('admin.analytics.title')}
          </h1>
          <p className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]">
            {t('admin.analytics.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Segmented label={t('admin.analytics.period.label')} testId="analytics-period">
            {ANALYTICS_PERIODS.map((p) => (
              <SegmentButton key={p} pressed={period === p} onClick={() => setPeriod(p)}>
                {t(`admin.analytics.period.d${p}`)}
              </SegmentButton>
            ))}
          </Segmented>
          <Segmented label={t('admin.analytics.app.label')} testId="analytics-app">
            {APPS.map((a) => (
              <SegmentButton key={a} pressed={app === a} onClick={() => setApp(a)}>
                {t(`admin.analytics.app.${a}`)}
              </SegmentButton>
            ))}
          </Segmented>
        </div>
      </div>

      {kpiSummary.isLoading ? (
        <KpiTilesSkeleton />
      ) : kpiSummary.isError ? (
        <PanelError error={kpiSummary.error} fallbackKey="admin.analytics.failed" />
      ) : kpiColumns ? (
        <KpiTiles columns={kpiColumns} />
      ) : null}

      <Card title={t('admin.analytics.chart.title')}>
        {summary.isLoading ? (
          <div className="h-[220px] animate-pulse rounded-cf-md bg-cf-fill" aria-busy="true" />
        ) : summary.isError ? (
          <PanelError error={summary.error} fallbackKey="admin.analytics.failed" />
        ) : chartEmpty ? (
          <PanelEmpty messageKey="admin.analytics.empty" />
        ) : (
          <DailyChart days={chartDays} />
        )}
      </Card>

      {/* Full width, stacked: the feed's eight columns need the whole row. */}
      <Card title={t('admin.analytics.sessions.title')}>
        <SessionsTable from={range.from} to={range.to} app={app} />
      </Card>
      <Card title={t('admin.analytics.users.title')}>
        <UsersLastSeen />
      </Card>
    </>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-3 lg:px-[18px] lg:py-4">
      <h2 className="text-base font-semibold tracking-[-0.02em] lg:text-[17px]">{title}</h2>
      {children}
    </section>
  );
}

function Segmented({ label, testId, children }: { label: string; testId: string; children: ReactNode }) {
  return (
    <div
      role="group"
      aria-label={label}
      data-testid={testId}
      className="flex w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px]"
    >
      {children}
    </div>
  );
}

function SegmentButton({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        'h-9 rounded-cf-md px-3 text-[13px] font-semibold whitespace-nowrap',
        pressed ? 'bg-cf-surface text-cf-ink' : 'bg-transparent text-cf-ink-muted hover:text-cf-ink',
      )}
    >
      {children}
    </button>
  );
}
